import React, { useState, Fragment, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Paper,
  LinearProgress,
  TextField
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  // ✅ CORREGIDO: Root dinámico según tema
  root: {
    "& .MuiDialog-paper": {
      minWidth: 500,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    },
  },
  // ✅ CORREGIDO: Content dinámico según tema
  content: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  // ✅ CORREGIDO: ProgressContainer dinámico según tema
  progressContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
  },
  // ✅ CORREGIDO: StatusText dinámico según tema
  statusText: {
    marginTop: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.primary,
  },
  alert: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
  },
  errorAlert: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  infoAlert: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  successAlert: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  updateButton: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  progressBar: {
    width: "100%",
    marginTop: theme.spacing(2),
  },
  stepsList: {
    marginTop: theme.spacing(2),
  },
  stepItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  stepIcon: {
    marginRight: theme.spacing(1),
  },
}));

const UpdateVersionModal = ({ open, onClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [versionInfo, setVersionInfo] = useState(null);
  const [error, setError] = useState(null);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(""); // Paso actual de la actualización
  // ✅ FASE 4: Estados para modal de token de GitHub
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [githubToken, setGithubToken] = useState("");
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  
  // ✅ REFS para limpiar intervalos
  const pollIntervalRef = useRef(null);
  
  // ✅ LIMPIAR INTERVALOS al desmontar componente
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const handleCheckUpdates = async () => {
    setLoading(true);
    setError(null);
    setUpdateStatus(null);
    setVersionInfo(null);

    try {
      // Obtener información de actualizaciones
      const { data: updateData } = await api.get("/system/check-updates");
      setUpdateStatus(updateData);
      
      // Obtener información de versiones semánticas
      try {
        const { data: versionData } = await api.get("/system/version-info");
        setVersionInfo(versionData);
      } catch (versionError) {
        console.log('⚠️ No se pudo obtener información de versiones:', versionError);
        // No es crítico, continuar sin información de versiones
      }
    } catch (err) {
      // ✅ MEJORAR MENSAJE DE ERROR: Usar información detallada del backend
      const errorData = err.response?.data || {};
      const errorInfo = {
        error: errorData.error || "Error al verificar actualizaciones",
        details: errorData.details || errorData.errorDetails || null,
        recommendation: errorData.recommendation || errorData.errorRecommendation || null,
        errorType: errorData.errorType || null
      };
      setError(errorInfo);
      
      // ✅ FASE 4: Si el error es de token inválido, mostrar modal para ingresar token
      if (errorData.errorType === "GITHUB_TOKEN_INVALID") {
        setShowTokenModal(true);
      } else {
        toastError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ FASE 4: Función para actualizar token de GitHub
  const handleUpdateGithubToken = async () => {
    const trimmedToken = githubToken.trim();
    
    // ✅ FASE 5: Validaciones mejoradas
    if (!trimmedToken) {
      setTokenError("El token es requerido");
      return;
    }

    // Validación de longitud mínima
    if (trimmedToken.length < 50) {
      setTokenError("El token parece ser demasiado corto. Un token válido de GitHub PAT tiene al menos 50 caracteres");
      return;
    }

    // Validación del formato del token (debe comenzar con github_pat_)
    if (!trimmedToken.startsWith("github_pat_")) {
      setTokenError("El token debe comenzar con 'github_pat_'. Verifica que estés usando un Personal Access Token (PAT) de GitHub");
      return;
    }

    // Validación de formato básico (github_pat_ seguido de caracteres alfanuméricos y guiones)
    const tokenPattern = /^github_pat_[A-Za-z0-9_-]+$/;
    if (!tokenPattern.test(trimmedToken)) {
      setTokenError("El formato del token no es válido. Asegúrate de copiar el token completo sin espacios adicionales");
      return;
    }

    setTokenLoading(true);
    setTokenError(null);

    try {
      await api.post("/system/update-github-token", {
        token: githubToken.trim()
      });

      // Si es exitoso, cerrar modal y reintentar verificación
      setShowTokenModal(false);
      setGithubToken("");
      setError(null);
      
      // Mostrar mensaje de éxito
      toast.success("✅ Token de GitHub actualizado exitosamente");
      
      // Reintentar verificación de actualizaciones
      await handleCheckUpdates();
    } catch (err) {
      const errorData = err.response?.data || {};
      setTokenError(errorData.error || errorData.details || "Error al actualizar el token");
      toastError(err);
    } finally {
      setTokenLoading(false);
    }
  };

  // ✅ NOTA: handleForceUpdate fue eliminada - El botón "FORZAR RECOMPILACIÓN" fue removido por UX
  // Si se necesita en el futuro, esta función puede restaurarse desde el historial de Git

  // ✅ POLLING: Verificar estado de actualización cada 3 segundos
  const pollUpdateStatus = () => {
    console.log("🚀 [POLLING] Iniciando polling de estado de actualización...");
    
    const checkStatus = async () => {
      try {
        console.log("📡 [POLLING] Verificando estado...");
        const { data: statusData } = await api.get("/system/update-status");
        
        console.log(`📊 [POLLING] Respuesta: status=${statusData.status}, progress=${statusData.progress}%, step="${statusData.currentStep}"`);
        
        // Actualizar progreso y estado
        setUpdateProgress(statusData.progress || 0);
        setCurrentStep(statusData.currentStep || "");
        
        if (statusData.status === 'completed') {
          console.log("✅ [POLLING] Actualización completada!");
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setUpdating(false);
          setUpdateStatus(prev => ({
            ...prev,
            updateCompleted: true,
            newVersion: statusData.result?.newVersion,
            newMessage: statusData.result?.newMessage,
            newAuthor: statusData.result?.newAuthor,
            newDate: statusData.result?.newDate,
            steps: statusData.steps || []
          }));
        } else if (statusData.status === 'error') {
          console.log("❌ [POLLING] Error detectado:", statusData.error);
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setUpdating(false);
          setError({
            error: "Error durante la actualización",
            details: statusData.error || "Error desconocido",
            recommendation: "Verifica los logs del servidor para más detalles."
          });
        } else if (statusData.status === 'running') {
          console.log(`🔄 [POLLING] En progreso... Steps: ${statusData.steps?.length || 0}`);
          // Actualizar pasos completados
          setUpdateStatus(prev => ({
            ...prev,
            steps: statusData.steps || prev?.steps || []
          }));
        }
      } catch (err) {
        // ✅ Manejar errores de conexión durante reinicio (esperados)
        if (err.code === 'ECONNABORTED' || 
            err.message?.includes('timeout') || 
            err.message?.includes('Network Error') ||
            !err.response) {
          // Error de conexión esperado durante reinicio - continuar polling
          console.log("🔄 [POLLING] Backend reiniciando, continuando polling...");
        } else {
          console.error("❌ [POLLING] Error verificando estado:", err);
        }
        // Continuar polling aunque falle (el backend se restaurará)
      }
    };
    
    // Verificar inmediatamente y luego cada 3 segundos
    checkStatus();
    pollIntervalRef.current = setInterval(checkStatus, 3000);
  };

  const handlePerformUpdate = async (forceUpdate = false) => {
    // Si no hay actualizaciones y no es forzado, mostrar error
    if (!updateStatus?.hasUpdates && !forceUpdate) {
      setError("No hay actualizaciones disponibles");
      return;
    }

    setUpdating(true);
    setError(null);
    setUpdateProgress(0);

    try {
      console.log("🚀 [UPDATE] Iniciando actualización completa...");
      // Iniciar actualización (responde inmediatamente)
      const { data } = await api.post("/system/perform-full-update", {
        previousVersion: updateStatus?.currentVersion || "N/A"
      });
      
      console.log("📨 [UPDATE] Respuesta del servidor:", data);
      
      if (data.status === 'running') {
        console.log("✅ [UPDATE] Servidor respondió 'running', iniciando polling...");
        // Iniciar polling para verificar progreso
        pollUpdateStatus();
        return; // Salir, el polling manejará el resto
      }

    } catch (err) {
      // Manejo especial para errores de conexión durante reinicio
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message?.includes('Network Error')) {
        // Esperar y verificar si la actualización se completó
        setTimeout(async () => {
          try {
            const { data: checkResult } = await api.get("/system/check-updates");
            // Si el sistema responde, la actualización fue exitosa
            const commitMessage = checkResult.currentCommitMessage || checkResult.currentMessage || "Actualización completada (conexión restaurada)";
            setUpdateProgress(100);
            setUpdateStatus({
              ...updateStatus,
              updateCompleted: true,
              newVersion: checkResult.currentVersion,
              newMessage: commitMessage,
              newAuthor: checkResult.currentAuthor || "Sistema",
              newDate: checkResult.currentDate || new Date().toLocaleDateString(),
              steps: ["✅ Actualización aplicada", "✅ Servicios reiniciados", "✅ Conexión restaurada"]
            });
          } catch (retryErr) {
            // Después del timeout, asumir que la actualización fue exitosa
            try {
              const { data: checkResult } = await api.get("/system/check-updates");
              const commitMessage = checkResult.currentCommitMessage || checkResult.currentMessage || "Actualización completada exitosamente";
              setError(null);
              setUpdateProgress(100);
              setUpdateStatus({
                ...updateStatus,
                updateCompleted: true,
                newVersion: checkResult.currentVersion || "Actualizada",
                newMessage: commitMessage,
                newAuthor: checkResult.currentAuthor || "Sistema", 
                newDate: checkResult.currentDate || new Date().toLocaleDateString(),
                steps: [
                  "✅ Código actualizado",
                  "✅ Dependencias actualizadas",
                  "✅ Backend compilado y reiniciado",
                  "✅ Servicios verificados",
                  "✅ Actualización completada (verificación manual recomendada)"
                ]
              });
            } catch (checkErr) {
              // Si no se puede obtener, usar mensaje genérico
              setError(null);
              setUpdateProgress(100);
              setUpdateStatus({
                ...updateStatus,
                updateCompleted: true,
                newVersion: "Actualizada",
                newMessage: "Actualización completada exitosamente",
                newAuthor: "Sistema", 
                newDate: new Date().toLocaleDateString(),
                steps: [
                  "✅ Código actualizado",
                  "✅ Dependencias actualizadas",
                  "✅ Backend compilado y reiniciado",
                  "✅ Servicios verificados",
                  "✅ Actualización completada (verificación manual recomendada)"
                ]
              });
            }
          }
        }, 10000); // Esperar 10 segundos antes de verificar
      } else {
        // ✅ MEJORAR MENSAJE DE ERROR: Usar información detallada del backend
        const errorData = err.response?.data || {};
        setError({
          error: errorData.error || "Error durante la actualización",
          details: errorData.details || errorData.errorDetails || null,
          recommendation: errorData.recommendation || errorData.errorRecommendation || null,
          errorType: errorData.errorType || null
        });
        toastError(err);
        setUpdateProgress(0);
        setUpdating(false);
      }
    }
  };

  const handleClose = () => {
    if (!updating) {
      setUpdateStatus(null);
      setError(null);
      setUpdateProgress(0);
      onClose();
      // Actualizar toda la página después de cerrar el modal
      setTimeout(() => {
        window.location.reload();
      }, 300); // Pequeño delay para que se cierre el modal primero
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box className={classes.progressContainer}>
          <CircularProgress />
          <Typography className={classes.statusText}>
            {i18n.t("updateVersion.status.checking")}
          </Typography>
        </Box>
      );
    }

    if (updating) {
      return (
        <Box className={classes.progressContainer}>
          <CircularProgress />
          <Typography className={classes.statusText}>
            Actualización completa en progreso...
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={updateProgress} 
            className={classes.progressBar}
          />
          <Typography variant="body2" className={classes.statusText}>
            {updateProgress}% completado
          </Typography>
          {currentStep && (
            <Typography variant="body2" className={classes.statusText} style={{ fontStyle: 'italic', marginTop: 8 }}>
              {currentStep}
            </Typography>
          )}
          {!currentStep && (
            <Typography variant="body2" className={classes.statusText}>
              Este proceso puede tomar varios minutos...
            </Typography>
          )}
          {updateStatus?.steps && updateStatus.steps.length > 0 && (
            <Box style={{ marginTop: 16, textAlign: 'left', maxHeight: 200, overflowY: 'auto' }}>
              {updateStatus.steps.map((step, index) => (
                <Typography key={index} variant="body2" style={{ fontSize: '0.85rem', marginBottom: 4, color: theme.palette.text.secondary }}>
                  {step}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    if (error) {
      // ✅ MEJORAR MENSAJE DE ERROR: Extraer información detallada del error
      let errorMessage = error;
      let errorDetails = null;
      let errorRecommendation = null;
      
      // Si el error es un objeto con información detallada
      if (typeof error === 'object' && error !== null) {
        errorMessage = error.error || error.message || "Error durante la actualización";
        errorDetails = error.details || error.errorDetails || null;
        errorRecommendation = error.recommendation || error.errorRecommendation || null;
      }
      
      return (
        <Paper className={`${classes.alert} ${classes.errorAlert}`} style={{ backgroundColor: theme.mode === 'dark' ? '#3a1a1a' : theme.palette.error.light, color: theme.palette.text.primary }}>
          <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary, fontWeight: 'bold' }}>
            <span role="img" aria-label="Error">❌</span> Error
          </Typography>
          <Typography variant="body1" style={{ color: theme.palette.text.primary, marginBottom: errorDetails || errorRecommendation ? 8 : 0 }}>
            {errorMessage}
          </Typography>
          {errorDetails && (
            <Typography variant="body2" style={{ color: theme.palette.text.primary, marginTop: 8, marginBottom: errorRecommendation ? 8 : 0, opacity: 0.9 }}>
              <strong>Detalles:</strong> {errorDetails}
            </Typography>
          )}
          {errorRecommendation && (
            <Typography variant="body2" style={{ color: theme.palette.text.primary, marginTop: 8, fontStyle: 'italic', opacity: 0.8 }}>
              <strong>Recomendación:</strong> {errorRecommendation}
            </Typography>
          )}
        </Paper>
      );
    }

    if (updateStatus?.updateCompleted) {
      return (
        <Paper className={`${classes.alert} ${classes.successAlert}`} style={{ backgroundColor: theme.mode === 'dark' ? '#1f3a1f' : theme.palette.success.light, color: theme.palette.text.primary }}>
          <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>
            <span role="img" aria-label="success">✅</span> Actualización Completada
          </Typography>
          <Typography variant="body2" paragraph style={{ color: theme.palette.text.primary }}>
            <strong>Nueva versión:</strong> {updateStatus.newVersion}
          </Typography>
          <Typography variant="body2" paragraph style={{ color: theme.palette.text.primary, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            <strong>Mensaje:</strong> {updateStatus.newMessage}
          </Typography>
          <Typography variant="body2" paragraph style={{ color: theme.palette.text.primary }}>
            <strong>Autor:</strong> {updateStatus.newAuthor}
          </Typography>
          {updateStatus.steps && updateStatus.steps.length > 0 && (
            <Box className={classes.stepsList}>
              <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>
                Pasos completados:
              </Typography>
              {updateStatus.steps.map((step, index) => (
                <Box key={index} className={classes.stepItem}>
                  <span role="img" aria-label="check" className={classes.stepIcon}>✅</span>
                  <Typography variant="body2" style={{ color: theme.palette.text.primary }}>{step}</Typography>
                </Box>
              ))}
            </Box>
          )}
          <Typography variant="body2" style={{ color: theme.palette.primary.main, fontWeight: "bold" }}>
            <span role="img" aria-label="check">✅</span> Actualización completada exitosamente. Los servicios se han reiniciado automáticamente.
          </Typography>
          <Typography variant="body2" style={{ color: theme.palette.text.secondary || theme.palette.text.primary, marginTop: "8px", fontSize: "0.875rem" }}>
            <span role="img" aria-label="idea">💡</span> Los usuarios experimentaron una breve reconexión automática (2-5 segundos). 
            Presiona "CERRAR" para recargar esta página.
          </Typography>
        </Paper>
      );
    }

    if (updateStatus) {
      return (
        <Box>
          {/* Información de Versiones Semánticas */}
          {versionInfo && versionInfo.semanticVersion && (
            <Paper style={{ 
              padding: "16px", 
              marginBottom: "20px", 
              backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa', 
              border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}` 
            }}>
              <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main }}>
                <span role="img" aria-label="version">📋</span> INFORMACIÓN DE VERSIONES
              </Typography>
              
              <Box style={{ marginBottom: "12px" }}>
                <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                  ├── Versión Actual: <strong>{versionInfo.semanticVersion.currentVersion}</strong>
                </Typography>
                <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                  ├── Sistema de Versiones: {versionInfo.semanticVersion.hasVersionSystem ? <><span role="img" aria-label="check mark">✅</span> Activo</> : <><span role="img" aria-label="cross mark">❌</span> Inactivo</>}
                </Typography>
                <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                  ├── Última Actualización: {versionInfo.semanticVersion.lastVersionUpdate}
                </Typography>
                <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                  └── Tags Git: {versionInfo.semanticVersion.gitTags.length} disponibles
                </Typography>
              </Box>
              
              {/* Historial de Versiones */}
              {versionInfo.semanticVersion.versionHistory && versionInfo.semanticVersion.versionHistory.length > 0 && (
                <Box style={{ marginTop: "12px" }}>
                  <Typography variant="subtitle2" gutterBottom style={{ color: theme.palette.text.secondary || theme.palette.text.primary }}>
                    <span role="img" aria-label="bar chart">📊</span> Historial de Versiones:
                  </Typography>
                  <Box style={{ maxHeight: "120px", overflowY: "auto", backgroundColor: theme.palette.background.paper, padding: "8px", borderRadius: "4px", border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}` }}>
                    {versionInfo.semanticVersion.versionHistory.slice(0, 5).map((version, index) => (
                      <Typography key={index} variant="body2" style={{ fontFamily: "monospace", fontSize: "0.75rem", color: theme.palette.text.secondary || theme.palette.text.primary }}>
                        • v{version.version} - {version.date}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          )}
          
          <Paper style={{ padding: "16px", marginBottom: "20px", backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa' }}>
            <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>
              Estado de Actualizaciones
            </Typography>
            <Typography variant="body1" paragraph style={{ color: theme.palette.text.primary }}>
              <strong>{i18n.t("updateVersion.messages.currentVersion")}</strong> {updateStatus.currentVersion}
            </Typography>
            {updateStatus.hasUpdates ? (
              <Typography variant="body1" paragraph style={{ color: theme.palette.text.primary }}>
                <strong>Última versión disponible:</strong> {updateStatus.latestMessage || updateStatus.latestCommitMessage || updateStatus.latestVersion}
              </Typography>
            ) : (
              <Typography variant="body1" paragraph style={{ color: theme.palette.text.primary }}>
                <strong>Descripción de la versión actual:</strong> {updateStatus.currentMessage || updateStatus.currentCommitMessage || "Sin descripción disponible"}
              </Typography>
            )}
            <Typography variant="body1" paragraph style={{ color: theme.palette.text.primary }}>
              <strong>Estado:</strong>{" "}
              {updateStatus.hasUpdates ? (
                <span style={{ color: theme.palette.success.main, fontWeight: "bold" }}>
                  <span role="img" aria-label="check">✅</span> {i18n.t("updateVersion.status.updatesAvailable")}
                </span>
              ) : (
                <span style={{ color: theme.palette.info.main, fontWeight: "bold" }}>
                  <span role="img" aria-label="check">✅</span> {i18n.t("updateVersion.status.upToDate")}
                </span>
              )}
            </Typography>
          </Paper>
          
          {updateStatus.hasUpdates && (
            <Paper className={`${classes.alert} ${classes.infoAlert}`} style={{ backgroundColor: theme.mode === 'dark' ? '#1a1f3a' : theme.palette.info.light, color: theme.palette.text.primary }}>
              <Typography variant="body2" style={{ color: theme.palette.text.primary }}>
                Hay {updateStatus.commitsAhead} nuevos commits disponibles.
              </Typography>
              
              {/* Información detallada del commit si está disponible */}
              {updateStatus.commitAnalysis && (
                <Box style={{ marginTop: "16px" }}>
                  <Typography variant="h6" gutterBottom style={{ color: theme.palette.primary.main }}>
                    <span role="img" aria-label="clipboard">📋</span> DETALLES DEL COMMIT DISPONIBLE:
                  </Typography>
                  <Box style={{ 
                    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa', 
                    padding: "12px", 
                    borderRadius: "8px",
                    marginBottom: "16px",
                    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`
                  }}>
                    <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                      ├── Commit: {updateStatus.latestVersion}
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                      ├── Mensaje: "{updateStatus.latestMessage}"
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                      ├── Archivos modificados: {updateStatus.commitAnalysis.files.length} archivo(s)
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                      ├── Tipo de cambio: {
                        updateStatus.commitAnalysis.isConfigOnly ? "Configuración Git" :
                        updateStatus.commitAnalysis.isDocumentationOnly ? "Documentación" :
                        updateStatus.commitAnalysis.isCodeChange ? "Código fuente" :
                        updateStatus.commitAnalysis.requiresDatabase ? "Base de datos" :
                        "Mixto"
                      }
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                      └── Impacto: {
                        updateStatus.commitAnalysis.impact === 'high' ? "Alto" :
                        updateStatus.commitAnalysis.impact === 'medium' ? "Medio" :
                        "Bajo"
                      } ({updateStatus.commitAnalysis.estimatedTime})
                    </Typography>
                  </Box>
                  
                </Box>
              )}
              
              {/* Información sobre reinicio de servicios */}
              <Paper style={{ 
                padding: "12px", 
                backgroundColor: theme.mode === 'dark' ? '#3a2a1a' : '#fff3e0', 
                border: `1px solid ${theme.mode === 'dark' ? '#ff9800' : '#ffb74d'}`,
                marginTop: "16px",
                marginBottom: "16px"
              }}>
                <Typography variant="body2" style={{ color: theme.mode === 'dark' ? '#ffb74d' : '#e65100', fontWeight: "bold" }}>
                  <span role="img" aria-label="warning">⚠️</span> Importante: Reinicio de Servicios
                </Typography>
                <Typography variant="body2" style={{ color: theme.mode === 'dark' ? '#ffcc80' : '#bf360c', fontSize: "0.875rem", marginTop: "4px" }}>
                  • La actualización reiniciará los servicios automáticamente<br/>
                  • Los usuarios experimentarán una breve reconexión (2-5 segundos)<br/>
                  • <span role="img" aria-label="idea">💡</span> Recomendado fuera de horas pico para minimizar impacto
                </Typography>
              </Paper>
              
              <Box style={{ display: "flex", justifyContent: "center", marginTop: "16px" }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handlePerformUpdate()}
                  className={classes.updateButton}
                  size="large"
                  style={{ minWidth: 250 }}
                >
                  <span role="img" aria-label="update">⚡</span> Actualizar Sistema
                </Button>
              </Box>
              

            </Paper>
          )}
          
          {/* ✅ DB-FIRST: Información de última actualización desde GitHub */}
          {!updateStatus.hasUpdates && updateStatus.currentDateTime && (
            <Paper className={classes.alert} style={{ 
              marginTop: "16px",
              backgroundColor: theme.mode === 'dark' ? '#1e3a5f' : '#e3f2fd',
              color: theme.palette.text.primary,
            }}>
              <Typography variant="h6" gutterBottom style={{ color: theme.palette.text.primary }}>
                <span role="img" aria-label="clock">🕐</span> Información de Última Actualización
              </Typography>
              <Typography variant="body2" paragraph style={{ color: theme.palette.text.primary }}>
                <strong>Última actualización desde GitHub:</strong>
              </Typography>
              <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary, marginBottom: "8px" }}>
                <span role="img" aria-label="calendar">📅</span> Fecha: {updateStatus.currentDate || 'N/A'}
              </Typography>
              <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary, marginBottom: "8px" }}>
                <span role="img" aria-label="clock">🕐</span> Hora: {updateStatus.currentDateTime ? new Date(updateStatus.currentDateTime).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'America/Lima' }) : 'N/A'}
              </Typography>
              <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary, marginBottom: "8px" }}>
                <span role="img" aria-label="user">👤</span> Autor: {updateStatus.currentAuthor || 'N/A'}
              </Typography>
              <Typography variant="body2" style={{ fontFamily: "monospace", color: theme.palette.text.primary }}>
                <span role="img" aria-label="bookmark">🔖</span> Commit: {updateStatus.currentVersion || 'N/A'}
              </Typography>
            </Paper>
          )}
        </Box>
      );
    }

    return (
      <Box style={{ textAlign: 'center', padding: '20px 0', backgroundColor: theme.palette.background.paper }}>
        <Box style={{ marginBottom: 24 }}>
          <span style={{ fontSize: '48px' }} role="img" aria-label="tools">🔧</span>
        </Box>
        
        <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
          Mantenimiento del Sistema
        </Typography>
        
        <Typography variant="body1" paragraph style={{ color: theme.palette.text.secondary || theme.palette.text.primary, lineHeight: 1.6 }}>
          Verifica si hay actualizaciones disponibles para mantener tu sistema Watoolx 
          funcionando de manera óptima y segura.
        </Typography>
        
        <Box style={{ 
          background: theme.mode === 'dark' ? '#374151' : '#e3f2fd', 
          padding: 16, 
          borderRadius: 8, 
          margin: '16px 0',
          border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#2196f3'}`
        }}>
          <Typography variant="body2" style={{ color: theme.mode === 'dark' ? '#60a5fa' : '#1976d2', fontWeight: 'bold' }}>
            <span role="img" aria-label="lightbulb">💡</span> ¿Qué incluyen las actualizaciones?
          </Typography>
          <Box component="ul" style={{ margin: '8px 0', paddingLeft: 20 }}>
            <Typography component="li" variant="body2" style={{ color: theme.mode === 'dark' ? '#93c5fd' : '#1976d2' }}>
              Mejoras de seguridad y estabilidad
            </Typography>
            <Typography component="li" variant="body2" style={{ color: theme.mode === 'dark' ? '#93c5fd' : '#1976d2' }}>
              Nuevas funcionalidades y correcciones
            </Typography>
            <Typography component="li" variant="body2" style={{ color: theme.mode === 'dark' ? '#93c5fd' : '#1976d2' }}>
              Optimizaciones de rendimiento
            </Typography>
          </Box>
        </Box>
        
        <Typography variant="body2" style={{ color: theme.palette.text.secondary || theme.palette.text.primary, fontStyle: 'italic' }}>
          Haz clic en "Verificar Actualizaciones" para comenzar
        </Typography>
      </Box>
    );
  };

  return (
    <Fragment>
    <Dialog 
      open={open} 
      onClose={handleClose} 
      className={classes.root}
      maxWidth="sm"
      fullWidth={false}
    >
      <DialogTitle style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <span style={{ fontSize: '24px' }} role="img" aria-label="rocket">🚀</span>
        <Typography variant="h5" style={{ fontWeight: 'bold', color: 'white' }}>
          Actualización del Sistema
        </Typography>
      </DialogTitle>
      
      <DialogContent className={classes.content} style={{ padding: '16px 20px', backgroundColor: theme.palette.background.paper }}>
        {renderContent()}
      </DialogContent>
      
      <DialogActions style={{ 
        padding: '12px 20px',
        borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#eee'}`,
        background: theme.mode === 'dark' ? '#1f2937' : '#f8f9fa'
      }}>
        <Button 
          onClick={handleClose} 
          color="secondary" 
          disabled={updating}
          variant="outlined"
          style={{ marginRight: 8 }}
        >
          {updating ? 'Procesando...' : 'Cerrar'}
        </Button>
        {!loading && !updateStatus && !updating && (
          <Button 
            onClick={handleCheckUpdates} 
            color="primary" 
            variant="contained"
            startIcon={<span role="img" aria-label="search">🔍</span>}
          >
            Verificar Actualizaciones
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {/* ✅ FASE 4: Modal para ingresar token de GitHub - FUERA del Dialog principal para que se muestre independientemente */}
    <Dialog
      open={showTokenModal}
      onClose={() => {
        setShowTokenModal(false);
        setGithubToken("");
        setTokenError(null);
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle style={{
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        color: 'white',
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <span style={{ fontSize: '24px' }} role="img" aria-label="key">🔑</span>
        <Typography variant="h6" style={{ fontWeight: 'bold', color: 'white' }}>
          Actualizar Token de GitHub
        </Typography>
      </DialogTitle>
      
      <DialogContent style={{ padding: '24px', backgroundColor: theme.palette.background.paper }}>
        <Typography variant="body1" paragraph style={{ color: theme.palette.text.primary, marginBottom: 16 }}>
          El token de GitHub Personal Access Token (PAT) ha expirado o es inválido. Por favor, ingresa un nuevo token para continuar.
        </Typography>
        
        {error && (
          <Paper style={{ 
            padding: '12px', 
            marginBottom: 16, 
            backgroundColor: theme.mode === 'dark' ? '#3a1a1a' : '#ffebee',
            border: `1px solid ${theme.palette.error.main}`
          }}>
            <Typography variant="body2" style={{ color: theme.palette.error.main }}>
              <strong>Error:</strong> {error.error}
            </Typography>
            {error.details && (
              <Typography variant="body2" style={{ color: theme.palette.error.main, marginTop: 8, fontSize: '0.875rem' }}>
                {error.details}
              </Typography>
            )}
          </Paper>
        )}

        <TextField
          fullWidth
          label="Token de GitHub (PAT)"
          type="password"
          value={githubToken}
          onChange={(e) => {
            setGithubToken(e.target.value);
            setTokenError(null);
          }}
          placeholder="github_pat_..."
          variant="outlined"
          error={!!tokenError}
          helperText={tokenError || "Ingresa tu Personal Access Token de GitHub"}
          disabled={tokenLoading}
          style={{ marginBottom: 16 }}
          InputProps={{
            style: { color: theme.palette.text.primary }
          }}
          InputLabelProps={{
            style: { color: theme.palette.text.secondary || theme.palette.text.primary }
          }}
        />

        <Box style={{ marginTop: 16, padding: '12px', backgroundColor: theme.mode === 'dark' ? '#374151' : '#f3f4f6', borderRadius: '8px', border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}` }}>
          <Typography variant="body2" style={{ color: theme.palette.text.secondary || theme.palette.text.primary, fontSize: '0.875rem', marginBottom: 8, fontWeight: 500 }}>
            <span role="img" aria-label="info">ℹ️</span> Información sobre el token:
          </Typography>
          <Typography variant="body2" component="div" style={{ color: theme.palette.text.secondary || theme.palette.text.primary, fontSize: '0.875rem', lineHeight: 1.6 }}>
            • El token debe comenzar con <code style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#ffffff', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>github_pat_</code>
          </Typography>
          <Typography variant="body2" component="div" style={{ color: theme.palette.text.secondary || theme.palette.text.primary, fontSize: '0.875rem', lineHeight: 1.6 }}>
            • Debe tener al menos 50 caracteres de longitud
          </Typography>
          <Typography variant="body2" component="div" style={{ color: theme.palette.text.secondary || theme.palette.text.primary, fontSize: '0.875rem', lineHeight: 1.6 }}>
            • Puedes crear un nuevo token en: <code style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#ffffff', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>GitHub → Settings → Developer settings → Personal access tokens</code>
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions style={{ 
        padding: '12px 24px',
        borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#eee'}`,
        background: theme.mode === 'dark' ? '#1f2937' : '#f8f9fa'
      }}>
        <Button
          onClick={() => {
            setShowTokenModal(false);
            setGithubToken("");
            setTokenError(null);
          }}
          color="secondary"
          disabled={tokenLoading}
          variant="outlined"
          style={{ marginRight: 8 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleUpdateGithubToken}
          color="primary"
          variant="contained"
          disabled={tokenLoading || !githubToken.trim()}
          startIcon={tokenLoading ? <CircularProgress size={16} /> : <span role="img" aria-label="save">💾</span>}
        >
          {tokenLoading ? "Actualizando..." : "Actualizar Token"}
        </Button>
      </DialogActions>
    </Dialog>
    </Fragment>
  );
};

export default UpdateVersionModal;
